import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiPatchFetcher, apiPutFetcher, fetchRest } from '@/common/api/fetcher'
import {
  ActivatePersonaVars,
  AdoptPersonaVars,
  DeactivatePersonaVars,
  ImportPersonaCatalogResponse,
  ImportPersonaCatalogVars,
  GetSitePersonaVars,
  ListPersonaCatalogResponse,
  ListPersonaCatalogVars,
  ListSitePersonaResponse,
  ListSitePersonaVars,
  PersonaAdminApiPath,
  PersonaApiPath,
  PersonaCatalogDetailResponse,
  PersonaDefinitionHistoryResponse,
  PersonaHistoryVars,
  PersonaInsightListResponse,
  PersonaInsightRunResponse,
  RunPersonaInsightVars,
  PersonaMeasurementConfigResponse,
  UpdatePersonaMeasurementConfigVars,
  PersonaHealthResponse,
  PersonaReadinessResponse,
  PersonaStatsResponse,
  PersonaStatsVars,
  PersonaTrendResponse,
  PersonaTrendVars,
  ResetSitePersonaVars,
  SitePersonaDetailResponse,
  SitePersonaRow,
  UpdatePersonaCatalogVars,
  UpdateSitePersonaVars
} from '@/common/api/params/persona'

// ---------------- 全体管理 ----------------

export const usePersonaCatalogApi = (vars: ListPersonaCatalogVars | undefined) => {
  return useSWR<ListPersonaCatalogResponse, Response, [string, ListPersonaCatalogVars] | undefined>(
    vars ? [PersonaAdminApiPath.LIST_CATALOG, vars] : undefined
  )
}

export const usePersonaReadinessApi = (enabled = true) => {
  return useSWR<PersonaReadinessResponse, Response, [string, object] | undefined>(
    enabled ? [PersonaAdminApiPath.GET_READINESS, {}] : undefined
  )
}

// ---------------- サイト ----------------

export const useSitePersonaListApi = (vars: ListSitePersonaVars | undefined) => {
  return useSWR<ListSitePersonaResponse, Response, [string, ListSitePersonaVars] | undefined>(
    vars ? [PersonaApiPath.LIST_TEMPLATES, vars] : undefined
  )
}

export const useSitePersonaDetailApi = (vars: GetSitePersonaVars | undefined) => {
  return useSWR<SitePersonaDetailResponse, Response, [string, GetSitePersonaVars] | undefined>(
    vars ? [PersonaApiPath.GET_TEMPLATE, vars] : undefined
  )
}

export const usePersonaStatsApi = (vars: PersonaStatsVars | undefined) => {
  return useSWR<PersonaStatsResponse, Response, [string, PersonaStatsVars] | undefined>(
    vars ? [PersonaApiPath.GET_STATS, vars] : undefined
  )
}

export const usePersonaHealthApi = (vars: PersonaStatsVars | undefined) => {
  return useSWR<PersonaHealthResponse, Response, [string, PersonaStatsVars] | undefined>(
    vars ? [PersonaApiPath.GET_HEALTH, vars] : undefined
  )
}

export const usePersonaTrendApi = (vars: PersonaTrendVars | undefined) => {
  return useSWR<PersonaTrendResponse, Response, [string, PersonaTrendVars] | undefined>(
    vars ? [PersonaApiPath.GET_TREND, vars] : undefined
  )
}

export const useAdoptPersonaApi = (options?: MutationOptions<SitePersonaRow>) => {
  return useCallMutation<SitePersonaRow, AdoptPersonaVars>(PersonaApiPath.ADOPT, options)
}

export const useActivatePersonaApi = (options?: MutationOptions<SitePersonaRow>) => {
  return useCallMutation<SitePersonaRow, ActivatePersonaVars>(PersonaApiPath.ACTIVATE, options)
}

export const useDeactivatePersonaApi = (options?: MutationOptions<SitePersonaRow>) => {
  return useCallMutation<SitePersonaRow, DeactivatePersonaVars>(
    PersonaApiPath.DEACTIVATE,
    options
  )
}

/** 閾値・施策例・表示名の調整（7.12）。送った項目だけ変わる */
export const useUpdateSitePersonaApi = (options?: MutationOptions<SitePersonaRow>) => {
  return useCallMutation<SitePersonaRow, UpdateSitePersonaVars>(
    PersonaApiPath.UPDATE,
    options,
    apiPatchFetcher
  )
}

/** カタログの既定値に戻す（＝カタログの最新版を取り込む） */
export const useResetSitePersonaApi = (options?: MutationOptions<SitePersonaRow>) => {
  return useCallMutation<SitePersonaRow, ResetSitePersonaVars>(PersonaApiPath.RESET, options)
}

/** カタログ取込。multipart なので共通の JSON fetcher は使えない */
const importCatalogFetcher = async (
  path: string,
  { arg }: { arg: ImportPersonaCatalogVars }
): Promise<ImportPersonaCatalogResponse> => {
  const formData = new FormData()
  formData.append('file', arg.file)
  return fetchRest<ImportPersonaCatalogResponse>(`${path}?dry_run=${arg.dry_run}`, {
    method: 'POST',
    body: formData
  })
}

export const useImportPersonaCatalogApi = () => {
  return useSWRMutation<
    ImportPersonaCatalogResponse,
    Response,
    string,
    ImportPersonaCatalogVars
  >(PersonaAdminApiPath.IMPORT_CATALOG, importCatalogFetcher)
}

// ------------------------------------------------------------------
// 定義の編集と変更履歴（openspec: persona-definition-editing）
// ------------------------------------------------------------------

/** カタログ1体の編集画面用。採用中のサイト数と直近の履歴を伴う */
export const usePersonaCatalogDetailApi = (vars?: { template_id: string }) => {
  return useSWR<PersonaCatalogDetailResponse>(
    vars ? PersonaAdminApiPath.GET_CATALOG_DETAIL.replace('{template_id}', vars.template_id) : null
  )
}

/** カタログの定義を編集する。採用済みサイトの条件式は書き換わらない */
export const useUpdatePersonaCatalogApi = (
  options?: MutationOptions<PersonaCatalogDetailResponse>
) => {
  return useCallMutation<PersonaCatalogDetailResponse, UpdatePersonaCatalogVars>(
    PersonaAdminApiPath.UPDATE_CATALOG,
    options,
    apiPatchFetcher
  )
}

/** カタログの変更履歴 */
export const usePersonaCatalogHistoryApi = (vars?: { template_id: string }) => {
  return useSWR<PersonaDefinitionHistoryResponse>(
    vars ? PersonaAdminApiPath.GET_CATALOG_HISTORY.replace('{template_id}', vars.template_id) : null
  )
}

// ------------------------------------------------------------------
// 効果測定の設定（対照群の比率・測定の窓）
// ------------------------------------------------------------------

/** 設定・分けた結果のプレビュー・変更履歴をまとめて取る */
export const usePersonaMeasurementConfigApi = (vars?: { site_id: number }) => {
  return useSWR<PersonaMeasurementConfigResponse>(
    vars
      ? PersonaApiPath.GET_MEASUREMENT_CONFIG.replace('{site_id}', String(vars.site_id))
      : null
  )
}

/**
 * 比率と窓を変える。**上書きせず履歴を進める**。
 *
 * 応答に更新後のプレビューと履歴が入るので、呼び出し側で再取得しなくてよい。
 */
export const useUpdatePersonaMeasurementConfigApi = (
  options?: MutationOptions<PersonaMeasurementConfigResponse>
) => {
  return useCallMutation<
    PersonaMeasurementConfigResponse,
    UpdatePersonaMeasurementConfigVars
  >(PersonaApiPath.UPDATE_MEASUREMENT_CONFIG, options, apiPutFetcher)
}

// ------------------------------------------------------------------
// 状況示唆（AI）
// ------------------------------------------------------------------

/** 保存済みの示唆（新しい順・最大10世代）。入力スナップショットは含まれない */
export const usePersonaInsightsApi = (vars?: { site_id: number }) => {
  return useSWR<PersonaInsightListResponse>(
    vars ? PersonaApiPath.LIST_INSIGHTS.replace('{site_id}', String(vars.site_id)) : null
  )
}

/**
 * 示唆を生成して1世代として保存する。
 *
 * 集計 → AIの解釈 → 保存の3段でAIを待つため**数十秒かかる**。
 * 呼び出し側で必ず進捗を出し、実行中はボタンを無効にすること。
 */
export const useRunPersonaInsightApi = (
  options?: MutationOptions<PersonaInsightRunResponse>
) => {
  return useCallMutation<PersonaInsightRunResponse, RunPersonaInsightVars>(
    PersonaApiPath.RUN_INSIGHT,
    options
  )
}

/** このサイトで行われた定義変更の履歴。他サイトの変更は含まれない */
export const useSitePersonaHistoryApi = (vars?: PersonaHistoryVars) => {
  return useSWR<PersonaDefinitionHistoryResponse>(
    vars
      ? PersonaApiPath.GET_HISTORY.replace('{site_id}', String(vars.site_id)).replace(
          '{persona_id}',
          vars.persona_id
        )
      : null
  )
}
